import { z } from 'zod';
import { BootService } from '@/services/boot';
import { ClientItemPath, PaginationResponse, ServiceError } from '@/types/service';
import { DsaReport, DsaReportSchema, Result } from '@/types/common';
import * as Type from '@/types/item';
import { ReturnStatus, ReturnStatusSchema } from '@/types/omsStatus';
import { requestHandler, responseHandler } from '@/utilities/service';
import { AxiosResponse } from 'axios';
import { environments } from '@/environment';

export async function getItem(id: number): Promise<Result<ServiceError, Type.Item>> {
  return requestHandler(BootService.axios.get(`/products/${id}`).then(responseHandler(Type.ItemSchema)));
}

export async function getHisto(id: number): Promise<Result<ServiceError, { data: Type.ItemHisto[] }>> {
  return requestHandler(
    BootService.axios
      .get(`/products/${id}/histories`)
      .then(responseHandler(z.object({ data: Type.ItemHistoSchema.array() }))),
  );
}

export async function getOmsReturnStatus(id: number): Promise<Result<ServiceError, { data: ReturnStatus[] }>> {
  return requestHandler(
    BootService.axios
      .get(`/products/${id}/return-statuses?omsVersion=2`)
      .then(responseHandler(z.object({ data: ReturnStatusSchema.array() }))),
  );
}

export async function getClientItems(
  id: number,
  path: ClientItemPath,
  page: number,
  perPage: number,
): Promise<Result<ServiceError, Type.ClientItems>> {
  return requestHandler(
    BootService.axios
      .get(`/customers/${id}/${path}?clientId=${id}&page=${page}&perPage=${perPage}`)
      .then(responseHandler(Type.ClientItemsSchema)),
  );
}

export async function getItemSizes(
  attrPath: Type.ItemAttrPath,
  productId: number,
  sectionId?: number,
): Promise<Result<ServiceError, { data: string[] }>> {
  return requestHandler(
    BootService.axios
      .get(`/products/${productId}/${attrPath}?sectionId=${sectionId}`)
      .then(responseHandler(z.object({ data: z.string().array() }))),
  );
}

export async function getItemAttributes(
  attrPath: Type.ItemAttrPath,
  productId: number,
  includeId: number,
  sectionId?: number,
): Promise<Result<ServiceError, { data: Type.ItemAttr[] }>> {
  const sectionIdPath = sectionId ? `&sectionId=${sectionId}` : '';
  return requestHandler(
    BootService.axios
      .get(`/products/${productId}/${attrPath}?includeId=${includeId === 0 ? '' : includeId}${sectionIdPath}`)
      .then(responseHandler(z.object({ data: Type.ItemAttrSchema.array() }))),
  );
}

export async function getItemSections(): Promise<Result<ServiceError, { data: Type.ItemAttr[] }>> {
  return requestHandler(
    BootService.axios.get(`/sections`).then(responseHandler(z.object({ data: Type.ItemAttrSchema.array() }))),
  );
}

export async function getItemBrands(brandId?: number): Promise<Result<ServiceError, { data: Type.ItemAttr[] }>> {
  return requestHandler(
    BootService.axios
      .get(`/brands?includeId=${brandId}`)
      .then(responseHandler(z.object({ data: Type.ItemAttrSchema.array() }))),
  );
}

export async function getItemModels(
  brandId: number,
  sectionId: number,
): Promise<Result<ServiceError, { data: Type.ItemAttr[] }>> {
  return requestHandler(
    BootService.axios
      .get(`/models?brandId=${brandId}&sectionId=${sectionId}`)
      .then(responseHandler(z.object({ data: Type.ItemAttrSchema.array() }))),
  );
}

export type UpdateItemAttributeData = {
  materialId: number;
  colorId: number;
  conditionId: number;
  size: string;
  brandId: number;
  sectionId: number;
  modelId: number | undefined;
};
export async function updateItemAttribute(
  id: number,
  data: UpdateItemAttributeData,
): Promise<Result<ServiceError, unknown>> {
  return requestHandler(BootService.axios.patch(`/products/${id}`, data).then(responseHandler(z.unknown())));
}

export type UpdateItemDescriptionData = {
  description: string;
  sourceLang: string;
};
export async function updateItemDescription(
  id: number,
  data: UpdateItemDescriptionData,
): Promise<Result<ServiceError, unknown>> {
  return requestHandler(
    BootService.axios.patch(`/products/${id}/descriptions`, data).then(responseHandler(z.unknown())),
  );
}

export async function updateItem(id: number, data: Partial<Type.Item>): Promise<Result<ServiceError, unknown>> {
  return requestHandler(BootService.axios.patch(`/products/${id}`, data).then(responseHandler(z.unknown())));
}

export async function cancelOrder(
  id: number,
  reasonId: string,
  comment?: string,
): Promise<Result<ServiceError, unknown>> {
  return requestHandler(
    BootService.axios
      .patch(`/products/${id}/cancel-purchase`, { reasonId, comment })
      .then(responseHandler(z.unknown())),
  );
}

export async function getItemReturnReasons(
  filter: Type.ItemReturnReasonFilter,
): Promise<Result<ServiceError, { data: Type.ItemReturnReason[] }>> {
  return requestHandler(
    BootService.axios
      .get(`/pickingCategories?filter=${filter}`)
      .then(responseHandler(z.object({ data: Type.ItemReturnReasonSchema.array() }))),
  );
}

export type ItemWarehousePickingData = {
  dueDate: string; // Date string in YYYY-MM-DD
  reasonId: number;
  comment: string;
  isReturnToSeller: boolean;
};
export async function itemWarehousePicking(
  id: number,
  data: ItemWarehousePickingData,
): Promise<Result<ServiceError, { data: Type.ItemReturnReference }>> {
  return requestHandler(
    BootService.axios
      .post(`/products/${id}/warehouse-picking`, data)
      .then(responseHandler(z.object({ data: Type.ItemReturnReferenceSchema }))),
  );
}

export type ItemRelistSellerData = {
  clientId: number;
  relistType: Type.ItemRelistType;
};
export async function itemRelistSeller(
  id: number,
  data: ItemRelistSellerData,
): Promise<Result<ServiceError, Type.NewProductId>> {
  return requestHandler(
    BootService.axios.post(`/products/${id}/relist`, data).then(responseHandler(Type.NewProductIdSchema)),
  );
}

export async function getItemRelistPrice(id: number): Promise<Result<ServiceError, { data: Type.ItemRelistPrice }>> {
  return requestHandler(
    BootService.axios
      .get(`/products/${id}/relistPrices`)
      .then(responseHandler(z.object({ data: Type.ItemRelistPriceSchema }))),
  );
}

export type ItemRelistBuyerData = {
  orderId: number;
  buyerId: number;
  relistFee: boolean;
};
export async function itemRelistBuyer(
  id: number,
  data: ItemRelistBuyerData,
): Promise<Result<ServiceError, { data: Type.NewIdProduct }>> {
  return requestHandler(
    BootService.axios
      .post(`/products/${id}/relist-to-buyer`, data)
      .then(responseHandler(z.object({ data: Type.NewIdProductSchema }))),
  );
}

export async function itemReleasePayment(id: number): Promise<Result<ServiceError, unknown>> {
  return requestHandler(BootService.axios.patch(`/products/${id}/release-payment`).then(responseHandler(z.unknown())));
}

export async function itemCancelEarlyPayout(id: number): Promise<AxiosResponse> {
  return BootService.mutationAxios.post(`/products/${id}/cancel-payout`);
}

export async function itemSendToWarehouse(id: number): Promise<Result<ServiceError, unknown>> {
  return requestHandler(
    BootService.axios.patch(`/products/${id}/send-to-warehouse`).then(responseHandler(z.unknown())),
  );
}

export async function itemSendToSeller(id: number): Promise<Result<ServiceError, unknown>> {
  return requestHandler(BootService.axios.patch(`/products/${id}/send-to-seller`).then(responseHandler(z.unknown())));
}

export async function getItemCurrencies(): Promise<Result<ServiceError, Type.ItemCurrency[]>> {
  return requestHandler(BootService.axios.get(`/currencies`).then(responseHandler(Type.ItemCurrencySchema.array())));
}

export async function itemLostInTransit(id: number): Promise<Result<ServiceError, unknown>> {
  return requestHandler(BootService.axios.patch(`/products/${id}/lost-in-transit`).then(responseHandler(z.unknown())));
}

export async function itemWithdrawFromSale(id: number): Promise<Result<ServiceError, unknown>> {
  return requestHandler(
    BootService.axios.patch(`/products/${id}/withdraw-from-sale`).then(responseHandler(z.unknown())),
  );
}

export async function itemCurationPriority(id: number): Promise<AxiosResponse> {
  return BootService.mutationAxios.put(`/products/${id}/curation-priority`);
}

export async function getItemCurationStatus(id: number): Promise<Type.ItemCurationStatus> {
  const res = await BootService.mutationAxios.get(`/products/${id}/deposit-matches`);
  return res.data;
}

export async function getItemMmaos(
  id: number,
  page: number,
  perPage: number,
): Promise<PaginationResponse<Type.ItemMmao>> {
  const res = await BootService.axios.get<PaginationResponse<Type.ItemMmao>>(
    `/products/${id}/mmao-negotiations?page=${page}&perPage=${perPage}`,
  );
  return res.data;
}

export async function getItemPhotos(id: number): Promise<Result<ServiceError, { data: Type.ItemPhoto[] }>> {
  return requestHandler(
    BootService.axios
      .get(`/products/${id}/photos`)
      .then(responseHandler(z.object({ data: Type.ItemPhotoSchema.array() }))),
  );
}

export async function uploadItemPhoto(id: number, image: File): Promise<Result<ServiceError, unknown>> {
  const formData = new FormData();
  formData.append('image', image);
  const headers = { ...BootService.getHeaders(), 'Content-Type': 'multipart/form-data' };
  return requestHandler(
    BootService.axios.post(`/products/${id}/photos`, formData, { headers }).then(responseHandler(z.unknown())),
  );
}

export async function deleteItemPhoto(id: number, photoName: string): Promise<Result<ServiceError, unknown>> {
  return requestHandler(
    BootService.axios.delete(`/products/${id}/photos`, { data: { photoName } }).then(responseHandler(z.unknown())),
  );
}

export async function reorderItemPhotos(id: number, photoList: string[]): Promise<Result<ServiceError, unknown>> {
  return requestHandler(
    BootService.axios.patch(`/products/${id}/photos`, { photoList }).then(responseHandler(z.unknown())),
  );
}

export type UpdateItemOrderCancellationData = {
  autoCancelSale: boolean;
};

export async function updateItemOrderCancellation(
  id: number,
  data: UpdateItemOrderCancellationData,
): Promise<Result<ServiceError, unknown>> {
  return requestHandler(
    BootService.axios.patch(`/products/${id}/auto-cancel-sale`, data).then(responseHandler(z.unknown())),
  );
}

export type UpdateItemInterceptionData = {
  orderId: number;
  date: string;
  comment: string;
};

export async function updateItemInterception(
  id: number,
  data: UpdateItemInterceptionData,
): Promise<Result<ServiceError, unknown>> {
  return requestHandler(
    BootService.axios.post(`/products/${id}/interception`, data).then(responseHandler(z.unknown())),
  );
}

export async function getItemCreateReturnLitigation({
  id,
  isReplacementTicket,
  clientType = Type.TimelineType.BUYER,
  litigation,
}: {
  id: number;
  isReplacementTicket: boolean;
  clientType?: Type.TimelineType | '';
  litigation?: string;
}): Promise<Result<ServiceError, Type.ItemCreateReturnLitigation>> {
  // Query object
  const query = new URLSearchParams();
  query.append('productId', id.toString());
  clientType && query.append('clientType', clientType);

  litigation && query.append('litigation', litigation);
  query.append('isReplacementTicket', isReplacementTicket.toString());

  return requestHandler(
    BootService.axios
      .get(`/unified-return-form?${query.toString()}`)
      .then(responseHandler(Type.ItemCreateReturnLitigationSchema)),
  );
}

export type CreateReturnLitigation = {
  description: string;
  isReplacementTicket: boolean;
  itemReceived: boolean;
  productId: number;
  reason: string;
  litigation?: string;
  clientType?: Type.TimelineType;
};

export const CreateReturnLitigationResponseSchema = z.object({
  zendeskTicketId: z.string().nullish().optional(),
  zendeskTicketLink: z.string().nullish().optional(),
});

export type CreateReturnLitigationResponse = z.infer<typeof CreateReturnLitigationResponseSchema>;

export async function createItemReturnLitigation(
  data: CreateReturnLitigation,
): Promise<Result<ServiceError, CreateReturnLitigationResponse>> {
  return requestHandler(
    BootService.axios.post(`/unified-return-form`, data).then(responseHandler(CreateReturnLitigationResponseSchema)),
  );
}

export type SubmitShippingLabel = {
  carrier: string;
  type: string;
};

export const SubmitShippingLabelSchema = z.object({
  link: z.string().nullish().optional(),
});

export type SubmitShippingLabelResponse = z.infer<typeof SubmitShippingLabelSchema>;

export async function submitShippingLabel(
  id: number,
  data: SubmitShippingLabel,
): Promise<Result<ServiceError, SubmitShippingLabelResponse>> {
  return requestHandler(
    BootService.axios.post(`/products/${id}/shipping-label`, data).then(responseHandler(SubmitShippingLabelSchema)),
  );
}

export async function getItemReturnFormLink(id: number): Promise<Result<ServiceError, Type.ItemReturnFormLink>> {
  return requestHandler(
    BootService.axios
      .get(`/unified-return-form/return-link?productId=${id}`)
      .then(responseHandler(Type.ItemReturnFormLinkSchema)),
  );
}

export async function getItemRequireAuthentication(
  id: number,
): Promise<Result<ServiceError, Type.ItemRequireAuthentication>> {
  return requestHandler(
    BootService.axios
      .get(`/products/${id}/authentication-required`)
      .then(responseHandler(Type.ItemRequireAuthenticationSchema)),
  );
}

export async function trackAction(
  productId: number,
  comment: Type.TrackingComment,
): Promise<Result<ServiceError, unknown>> {
  return requestHandler(
    BootService.axios.post(`/products/${productId}/action-tracking`, { comment }).then(responseHandler(z.unknown())),
  );
}

export async function trackClientAction(
  clientId: number,
  comment: Type.TrackingComment,
): Promise<Result<ServiceError, unknown>> {
  return requestHandler(
    BootService.axios.post(`/customers/${clientId}/action-tracking`, { comment }).then(responseHandler(z.unknown())),
  );
}

export async function getActionLogs(
  type: Type.ActionLogType,
  id: number,
  page: number,
  perPage: number,
): Promise<PaginationResponse<Type.ItemActionLog>> {
  const res = await BootService.axios.get<PaginationResponse<Type.ItemActionLog>>(
    `/action-logs/?type=${type}&id=${id}&page=${page}&perPage=${perPage}`,
  );
  return res.data;
}

export async function getCancelReasons(): Promise<Result<ServiceError, { data: Type.CancelReason[] }>> {
  return requestHandler(
    BootService.axios
      .get(`/unavailabilityReasons`)
      .then(responseHandler(z.object({ data: Type.CancelReasonSchema.array() }))),
  );
}

export type RequestNonSponsoredShippingLabelResponse = {
  url: string;
};

export async function requestNonSponsoredShippingLabel(
  productId: number,
): Promise<RequestNonSponsoredShippingLabelResponse> {
  const res = await BootService.mutationAxios.post(`/products/${productId}/shipments/document`);
  return res.data;
}

export async function getCarriers(originCountry: string): Promise<Result<ServiceError, Type.CarrierResponse[]>> {
  let url = '/carriers';
  if (originCountry != '') {
    url += `?originCountry=${originCountry}`;
  }
  return requestHandler(BootService.axios.get(url).then(responseHandler(Type.CarrierResponseSchema.array())));
}

export async function getDsaReportsByProductId(productId: number): Promise<Result<ServiceError, DsaReport[]>> {
  return requestHandler(
    BootService.axios
      .get(`/dsa/reports?id_product_reported=${productId}`)
      .then(responseHandler(DsaReportSchema.array())),
  );
}

export async function getPostalLitigation(productId: string): Promise<Result<ServiceError, Type.ItemPostalLitigation>> {
  return requestHandler(
    BootService.axios
      .get(`/products/${productId}/oms/postal-litigation`)
      .then(responseHandler(Type.ItemPostalLitigationSchema)),
  );
}

export async function confirmLitigation(payload: {
  productId: string;
  moment: string;
  time: string;
}): Promise<Result<ServiceError, unknown>> {
  return requestHandler(
    BootService.axios
      .patch(`/products/${payload.productId}/postal-litigation/start`, { moment: payload.moment, time: payload.time })
      .then(responseHandler(z.unknown())),
  );
}

export async function confirmDelivered({
  tmsShipmentId,
  buyerId,
  productId,
}: {
  tmsShipmentId: number;
  buyerId: number;
  productId: number;
}): Promise<Result<ServiceError, unknown>> {
  return requestHandler(
    BootService.axios
      .post(`/products/${productId}/confirm-delivered`, { tmsShipmentId, buyerId })
      .then(responseHandler(z.unknown())),
  );
}

export async function getPLDecisions(productId: number): Promise<Result<ServiceError, Type.PLDecisionResponse[]>> {
  return requestHandler(
    BootService.axios
      .get(`/products/${productId}/postal-litigation/decisions`)
      .then(responseHandler(Type.PLDecisionResponseSchema.array())),
  );
}

export async function submitPLDecision(payload: {
  productId: number;
  action: string;
  decisionComment: string;
  type: string;
}): Promise<Result<ServiceError, unknown>> {
  return requestHandler(
    BootService.axios
      .patch(`/products/${payload.productId}/postal-litigation/decisions`, {
        decisionType: payload.type,
        decisionValue: payload.action,
        decisionComment: payload.decisionComment,
      })
      .then(responseHandler(z.unknown())),
  );
}

export async function getPayByLinkDetail(productId: number): Promise<Result<ServiceError, Type.PayByLinkDetail>> {
  return requestHandler(
    BootService.axios.get(`/products/${productId}/pay-by-link`).then(responseHandler(Type.PayByLinkDetailSchema)),
  );
}

export async function getPayByLinkForm(productId: number): Promise<Result<ServiceError, Type.PayByLinkForm>> {
  return requestHandler(
    BootService.axios.get(`/products/${productId}/pay-by-link/form`).then(responseHandler(Type.PayByLinkFormSchema)),
  );
}

export type PayByLinkSubmitPayload = {
  currency: string;
  amount: string;
  reason: string;
};

export async function submitPayByLink(
  productId: number,
  data: PayByLinkSubmitPayload,
): Promise<Result<ServiceError, unknown>> {
  return requestHandler(
    BootService.axios.post(`/products/${productId}/pay-by-link`, data).then(responseHandler(z.unknown())),
  );
}

export async function deletePayByLink(productId: number): Promise<Result<ServiceError, unknown>> {
  return requestHandler(
    BootService.axios.delete(`/products/${productId}/pay-by-link`).then(responseHandler(z.unknown())),
  );
}

export async function getPayoutStatusDetail(payoutId: number): Promise<Result<ServiceError, unknown>> {
  return requestHandler(BootService.axios.get(`/payouts/${payoutId}`).then(responseHandler(z.unknown())));
}

export const GenerateUrlResponseSchema = z.object({
  link: z.string(),
});

export async function generateInvoiceRequestUrl(
  productId: number,
  clientId: number,
): Promise<Result<ServiceError, { link: string }>> {
  return requestHandler(
    BootService.axios
      .get(`/products/${productId}/invoice-link?clientId=${clientId}`)
      .then(responseHandler(GenerateUrlResponseSchema)),
  );
}

export async function getShippingLabelCarriers(
  productId: number,
  direction: Type.ShipmentDirection,
): Promise<Result<ServiceError, Type.ShippingLabelCarrier[]>> {
  return requestHandler(
    BootService.axios
      .get(`/shipments/carriers?productId=${productId}&direction=${direction}`)
      .then(responseHandler(Type.ShippingLabelCarrierSchema.array())),
  );
}

export type CreateShippingLabelData = {
  carrierName: string;
  dropOffType: string;
  shipmentLabelType: string;
  direction: Type.ShipmentDirection;
};

export const CreateShippingLabelResponseSchema = z.object({
  shipmentId: z.number(),
});

export type CreateShippingLabelResponse = z.infer<typeof CreateShippingLabelResponseSchema>;

export async function createShippingLabel(
  productId: number,
  data: CreateShippingLabelData,
): Promise<Result<ServiceError, CreateShippingLabelResponse>> {
  return requestHandler(
    BootService.axios
      .post(`/products/${productId}/shipments`, data)
      .then(responseHandler(CreateShippingLabelResponseSchema)),
  );
}

export const ShippingConvenienceLabelSchema = z.object({
  shipmentId: z.number(),
  labelType: z.enum(['printable_label', 'shipping_code']),
  documentType: z.string().nullish().optional(),
  documentUrl: z.string().nullish().optional(),
  singleShippingLabelUrl: z.string().nullish().optional(),
  chosenCarrier: z
    .object({
      name: z.string(),
      iconUrl: z.string().nullish().optional(),
      legacyId: z.number().nullish().optional(),
    })
    .nullish()
    .optional(),
  trackingReference: z.string().nullish().optional(),
  shipByDate: z.string().nullish().optional(),
});

export type ShippingConvenienceLabel = z.infer<typeof ShippingConvenienceLabelSchema>;

export async function getShippingConvenienceLabel(
  productId: number,
  direction: Type.ShipmentDirection,
): Promise<Result<ServiceError, ShippingConvenienceLabel>> {
  return requestHandler(
    BootService.axios
      .get(`/products/${productId}/shipping-convenience-label?direction=${direction}`)
      .then(responseHandler(ShippingConvenienceLabelSchema)),
  );
}

export async function getShippingReminder(productId: number): Promise<Type.ShippingReminder> {
  const res = await BootService.axios.get<Type.ShippingReminder>(`/products/${productId}/shipment-timestamps`);
  return res.data;
}

/*
  This headers is required due to new apis migration
  TODO: Must be removed, for Guillaume to check on the apis
*/
const HARDCODED_HEADERS = {
  'x-vc-language': 'en',
  'x-vc-currency': 'EUR',
  'x-vc-siteId': '1',
};

export async function getPurchasePlaces(): Promise<Array<Type.PurchasePlace>> {
  // This is public api which not required credentials
  // Use this public api instead duplicate one for papi
  const res = await BootService.axios.get<AxiosResponse<Array<Type.PurchasePlace>>>(
    `/product-listing/purchase-places`,
    {
      baseURL: environments[BootService.env].publicBaseUrl,
      headers: {
        ...HARDCODED_HEADERS,
      },
    },
  );

  return res.data.data;
}

export async function getAuthenticationAskInfo(productId: number): Promise<Type.AuthenticationAskInfo[]> {
  const res = await BootService.axios.get<AxiosResponse<Type.AuthenticationAskInfo[]>>(
    `/authentication/ask-sellers?productId=${productId}`,
    {
      baseURL: environments[BootService.env].privateBaseURL,
      headers: {
        ...HARDCODED_HEADERS,
      },
    },
  );
  return res.data.data;
}

export type SubmitAuthenticationAskInfoPayload = {
  file?: File | null;
  location?: string;
  date?: string;
};

export async function submitAuthenticationAskInfo(productId: number, data: SubmitAuthenticationAskInfoPayload) {
  const payload = new FormData();

  if (data.file) payload.append('invoice', data.file);
  if (data.location) payload.append('purchasePlaceId', data.location.toString());
  if (data.date) payload.append('purchaseDate', data.date);

  payload.append('withoutInfo', '0');

  return BootService.mutationAxios.post(`/authentication/ask-sellers/${productId}/submission`, payload, {
    baseURL: environments[BootService.env].privateBaseURL,
    headers: {
      'Content-Type': 'multipart/form-data',
      ...HARDCODED_HEADERS,
    },
  });
}

export async function getSellerShippingStatus(productId: number, types?: string[]): Promise<Type.SellerShippingStatus> {
  const res = await BootService.axios.get<Type.SellerShippingStatus>(`/products/${productId}/seller-shipping-status`, {
    params: {
      components: types?.length ? types : undefined,
    },
  });
  return res.data;
}

export async function restartOmsWorkflow(productId: number): Promise<Type.RestartOmsWorkflow> {
  const res = await BootService.mutationAxios.post<Type.RestartOmsWorkflow>(`/products/${productId}/return/restart`);
  return res.data;
}
